import React, { useState, useEffect } from "react";
import { Zap, Pencil } from "lucide-react";
import { ChatBox, Message } from "./ChatBox";
import {
  createThread,
  addMessageToThread,
  createRun,
  retrieveRun,
  listThreadMessages,
} from "@/utils/openai";
import { formatMarkdown } from "@/utils/markdown";
import { toast } from "@/components/ui/use-toast";
import { set } from "date-fns";

export const AssistantInput: React.FC = () => {
  const [query, setQuery] = useState("");
  const [chatVisible, setChatVisible] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [followUpMessage, setFollowUpMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [threadId, setThreadId] = useState<string | null>();

  const initializeThread = async () => {
    if (!threadId) {
      try {
        const newThreadId = await createThread();
        console.log("newThreadId:", newThreadId);
        setThreadId(newThreadId);
        return newThreadId; // Return the new thread ID
      } catch (error) {
        console.error("Failed to create thread:", error);
        return null;
      }
    }
    return threadId; // Return existing thread ID if it exists
  };

  // Initialize thread on component mount if needed
  // useEffect(() => {
  //   initializeThread();
  // }, []); // Only run once on component mount

  // Poll for run completion
  const pollRunCompletion = async (threadId: string, runId: string) => {
    const maxAttempts = 60; // 30 seconds with 500-milliseconds intervals
    let attempts = 0;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        throw new Error("Run timed out");
      }

      const run = await retrieveRun(threadId, runId);
      attempts++;

      if (run.status === "completed") {
        // Get the latest messages after run completion
        const threadMessages = await listThreadMessages(threadId);

        // Get the assistant's response (latest message with role 'assistant')
        const assistantMessages = threadMessages.filter(
          (msg) => msg.role === "assistant"
        );
        if (assistantMessages.length > 0) {
          const latestAssistantMessage = assistantMessages[0]; // They're ordered by recency

          // Extract text content
          const textContent = latestAssistantMessage.content
            .filter((content) => content.type === "text")
            .map((content) => content.text.value)
            .join("\n");

          // Add assistant message to UI - preserving markdown formatting
          const assistantMessage: Message = {
            id: latestAssistantMessage.id,
            content: textContent,
            isUser: false,
            isMarkdown: true,
          };

          setMessages((prev) => [...prev, assistantMessage]);
        }

        return true;
      } else if (
        run.status === "failed" ||
        run.status === "cancelled" ||
        run.status === "expired"
      ) {
        throw new Error(`Run failed with status: ${run.status}`);
      } else {
        // Still running, wait and check again
        await new Promise((resolve) => setTimeout(resolve, 500));
        return poll();
      }
    };

    return poll();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    // prevent the form from submitting
    e.preventDefault();
    if (!query.trim()) return;

    if (!chatVisible) {
      setChatVisible(true);
    }

    // Get the current or new thread ID
    const currentThreadId = await initializeThread();
    console.log("handle submit threadId:", currentThreadId);

    if (!currentThreadId) {
      toast({
        title: "Error",
        description: "Failed to create conversation thread",
        variant: "destructive",
      });
      return;
    }

    // Add user message to UI
    const userMessage: Message = {
      id: Date.now().toString(),
      content: query,
      isUser: true,
      isMarkdown: false,
    };

    setMessages((prev) => [...prev, userMessage]);
    setQuery("");
    setLoading(true);

    try {
      // Add message to thread - using the currentThreadId directly
      await addMessageToThread(currentThreadId, query);

      // Create a run with the assistant
      const runId = await createRun(currentThreadId);

      // Poll for completion
      await pollRunCompletion(currentThreadId, runId);
    } catch (error) {
      console.error("Error in conversation:", error);
      toast({
        title: "Error",
        description: "Failed to get a response from OpenAI",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFollowUpSubmit = async () => {
    if (!followUpMessage.trim() || !threadId) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      content: followUpMessage,
      isUser: true,
      isMarkdown: false,
    };

    setMessages((prev) => [...prev, userMessage]);
    setFollowUpMessage("");
    setLoading(true);

    try {
      // Add follow-up message to thread
      await addMessageToThread(threadId, followUpMessage);

      // Create a run with the assistant
      const runId = await createRun(threadId);

      // Poll for completion
      await pollRunCompletion(threadId, runId);
    } catch (error) {
      console.error("Error in conversation:", error);
      toast({
        title: "Error",
        description: "Failed to get a response from OpenAI",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseChat = () => {
    setChatVisible(false);
    setMessages([]);
  };

  return (
    <div className="flex flex-col items-center w-full max-w-[800px]">
      {!chatVisible ? (
        <form
          onSubmit={handleSubmit}
          className="flex items-center bg-[rgba(255,255,255,0.3)] backdrop-blur-sm w-full max-w-[600px] p-2 rounded-full shadow-sm border border-white/20 mb-6"
        >
          <div className="flex items-center justify-center w-10 h-10 text-[rgba(0,0,0,0.5)] ml-1">
            <Zap className="w-5 h-5" />
          </div>
          <input
            type="text"
            placeholder="More questions! Ask my personal assistant"
            className="flex-1 text-[rgba(0,0,0,0.7)] text-base p-2.5 border-none bg-transparent outline-none"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Ask assistant"
          />
          <button
            type="submit"
            className="flex items-center justify-center w-10 h-10 text-[rgba(0,0,0,0.5)] bg-transparent border-none cursor-pointer mr-1"
          >
            <Pencil className="w-5 h-5" />
          </button>
        </form>
      ) : (
        <ChatBox
          messages={messages}
          newMessage={followUpMessage}
          onNewMessageChange={setFollowUpMessage}
          onSendMessage={handleFollowUpSubmit}
          onClose={handleCloseChat}
          isLoading={loading}
        />
      )}
    </div>
  );
};
